import Survicate from '@survicate/survicate-web-package';
import * as React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import LoadingCircular from '../../components/LoadingCircular';
import { PATHS } from '../../config/consts';
import { isArrayEmpty } from '../../helpers/array';
import { selectors as authSelectors } from '../../modules/auth';
import commonActions, { selectors as commonSelectors } from '../../modules/common';
import { Roles } from '../../modules/commonTypes';
import { selectors as companySelectors } from '../../modules/company';
import localizationActions, { selectors as localizationSelectors } from '../../modules/localization';
import { selectors as siteSelectors } from '../../modules/sites';
import { selectors as userSelectors } from '../../modules/user';
import { OryLoginType } from '../../ory/types';

export interface IAppContextData {
  accessToken?: string;
  currentUserLang: string | null;
  feSiteRoles: {
    isCompanyAdmin: boolean;
    isSiteAdmin: boolean;
    isSiteAdminPayment: boolean;
    isSiteUser: boolean;
  };
  globalFeatures: Array<{ name: string }>;
  loggedIn: boolean;
  userRoles: Roles[];
  isUserLoggedAsOrySso?: boolean;
}

export const AppContext = createContext<IAppContextData>({
  accessToken: undefined,
  currentUserLang: null,
  feSiteRoles: {
    isCompanyAdmin: false,
    isSiteAdmin: false,
    isSiteAdminPayment: false,
    isSiteUser: false,
  },
  globalFeatures: [],
  isUserLoggedAsOrySso: false,
  loggedIn: false,
  userRoles: [],
});

export const useAppContext = () => useContext(AppContext);

export function AppContextProvider({ children }: React.PropsWithChildren) {
  const dispatch = useDispatch();
  const accessToken = useSelector(authSelectors.getAccessTokenSelector);
  const currentUserLang = useSelector(localizationSelectors.getCurrentUserLanguage);
  const feSiteRoles = useSelector(userSelectors.getCurrentUserRoles);
  const globalFeatures = useSelector(commonSelectors.getGlobalFeatures);
  const isLoadingFeatures = useSelector(commonSelectors.isFetchingFeatures);
  const loggedIn = useSelector(authSelectors.isLoggedInSelector);
  const isUserLoggedAsOrySso = useSelector(authSelectors.getOryLoginTypeSelector) === OryLoginType.Sso;
  const userRoles = useSelector(userSelectors.getUserRoles);
  const [isContextProviderInitialized, setIsContextProviderInitialized] = React.useState(false);
  const getCurrentUser = useSelector(userSelectors.getCurrentUserSelector);
  const siteData = useSelector(siteSelectors.getSiteData);
  const companyId = useSelector(companySelectors.getCompanyId);
  const companyName = useSelector(companySelectors.getCompanyName);
  const [survicateInitialized, setSurvicateInitialized] = useState(false);
  const isE2E = document.cookie.includes('e2e=true');

  useEffect(() => {
    setIsContextProviderInitialized(true);
  }, []);

  useEffect(() => {
    window.onstorage = (event) => {
      const parseToJson = (data: string | null) => {
        try {
          if (!data) {
            return {};
          }
          return JSON.parse(data);
        } catch (_) {
          return {};
        }
      };

      if (event.key === 'state') {
        const oldAccessToken = parseToJson(event.oldValue)?.auth?.accessToken || '';
        const newAccessToken = parseToJson(event.newValue)?.auth?.accessToken || '';
        if (oldAccessToken && oldAccessToken !== newAccessToken) {
          window.location.reload();
        }
      }
    };

    return () => {
      window.onstorage = null;
    };
  }, []);

  useEffect(() => {
    if (isArrayEmpty(globalFeatures)) {
      dispatch(commonActions.loadGlobalFeatures());
    }
    if (loggedIn && !window.location.href.includes(PATHS.ACCOUNT_ACTIVATION)) {
      dispatch(localizationActions.getCurrentUserLanguage());
    }
  }, [loggedIn]);

  useEffect(() => {
    if (isE2E) return;

    const availableData =
      loggedIn &&
      companyId &&
      companyName &&
      siteData &&
      userRoles &&
      getCurrentUser &&
      siteData.id &&
      !survicateInitialized;

    if (availableData) {
      Survicate.init({
        traits: {
          company_id: companyId,
          company_name: companyName,
          isApartmentAdmin: !!userRoles.some((role) => role === 'APARTMENT_ADMIN'),
          site_id: siteData.id,
          site_name: siteData.name,
          site_type: siteData.type,
          user_email: getCurrentUser.email,
          user_id: getCurrentUser.id,
          user_name: getCurrentUser.firstName + ' ' + getCurrentUser.lastName,
          user_role: userRoles.join(','),
        },
        workspaceKey: '5d440ad5cd7e3b89754e7733d94b6f9e',
      });
      setSurvicateInitialized(true);
    }
  }, [loggedIn, siteData, userRoles, companyId, companyName, getCurrentUser]);

  const contextValue = React.useMemo(
    () => ({
      accessToken,
      currentUserLang,
      feSiteRoles,
      globalFeatures,
      isUserLoggedAsOrySso,
      loggedIn,
      userRoles,
    }),
    [accessToken, currentUserLang, feSiteRoles, globalFeatures, isUserLoggedAsOrySso, loggedIn, userRoles]
  );

  return (
    <AppContext.Provider value={contextValue}>
      {isLoadingFeatures || !isContextProviderInitialized ? <LoadingCircular /> : children}
    </AppContext.Provider>
  );
}
